import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiRequest } from '../../utils/api';
import { getOKDCOrderInfo } from '../../utils/okdcApi';
import { getMetacityOrderInfo } from '../../utils/metacityApi';
import { fetchWowposOrderHistory, fetchWowposAppNotifications, resolveWowposCall, dismissWowposOrderEvent, dismissWowposReview } from '../../utils/wowposApi';
import { fetchTableOrderDetail } from '../../utils/unionApi';
import { getTossOrderInfo } from '../../utils/tossApi';
import { buildLines } from '../../utils/orderCellLines';
import wsService from '../../utils/websocket';
import OrderHistory from '../orderHistory';
import WowposNotifyLayer from './WowposNotifyLayer';
import backIcon from '../../assets/icons/common/back_icon.png';
import posOrderSound from '../../assets/audio/pos-order.wav';
import posCallSound from '../../assets/audio/pos-call.wav';
import posReviewSound from '../../assets/audio/pos-review.wav';

/**
 * 관리자 주문내역 총괄 페이지 — 모든 POS(유니온/오케이/와우포스) 공통.
 * QR 연동 테이블 전체를 셀 그리드로 표시(메뉴명+수량, 옵션 제외, 6줄 초과 시 '…').
 * 셀 클릭 시 손님 주문내역 UI(orderHistory)로 옵션 포함 상세 표시.
 * 열기/뒤로가기 패턴은 storeSettings.jsx(StoreSettings ↔ TableQrManager)와 동일.
 *
 * Props:
 *   - isOpen: boolean
 *   - onClose: () => void
 *   - storeId: number   (storeInfo.store_id)
 *   - posType: string   ('okdc' | 'wowpos' | 'unionpos' ...)
 */

// 동시성 제한 map — 테이블마다 라이브 POS API를 프록시 호출하므로 부하 제한.
async function mapLimit(items, limit, fn) {
  const results = new Array(items.length);
  let cursor = 0;
  const worker = async () => {
    while (cursor < items.length) {
      const idx = cursor++;
      results[idx] = await fn(items[idx], idx);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
}

// POS별 현재 주문 → 셀 라인용 menuItems([{name, quantity}]) 로 정규화 (옵션 무시).
async function fetchTableMenuItems(posType, storeId, tableCode) {
  try {
    if (posType === 'okdc') {
      const r = await getOKDCOrderInfo(storeId, tableCode);
      if (!r.success) return [];
      return (r.orders || []).flatMap((o) =>
        (o.items || []).map((it) => ({ name: it.prodName, quantity: Number(it.prodCount) || 1 }))
      );
    }
    if (posType === 'wowpos') {
      const r = await fetchWowposOrderHistory(storeId, tableCode);
      if (!r.success) return [];
      return (r.orders || []).flatMap((o) =>
        (o.items || []).map((it) => ({ name: it.name, quantity: Number(it.qty ?? it.quantity) || 1 }))
      );
    }
    if (posType === 'toss') {
      const r = await getTossOrderInfo(storeId, tableCode);
      if (!r.success) return [];
      return (r.orders || []).flatMap((o) =>
        (o.items || []).map((it) => ({ name: it.name, quantity: Number(it.quantity) || 1 }))
      );
    }
    if (posType === 'metacity') {
      // 단일 테이블(8060). 최초 로드는 loadOverview 가 8062 벌크로 처리하고,
      // 실시간 갱신처럼 테이블 1개만 다시 읽을 때 이 경로를 탄다.
      const r = await getMetacityOrderInfo(storeId, tableCode);
      if (!r.success) return [];
      return (r.orders || []).flatMap((o) =>
        (o.items || []).map((it) => ({ name: it.prodName, quantity: Number(it.prodCount) || 1 }))
      );
    }
    // union (기본) — storeCode는 unionApi가 로그인 토큰에서 자동 해석.
    const r = await fetchTableOrderDetail(tableCode);
    if (!r.success) return [];
    return (r.orderList || []).map((it) => ({ name: it.ItemName, quantity: Number(it.Qty) || 1 }));
  } catch (e) {
    console.error('[주문내역 개요] 테이블 조회 실패:', tableCode, e);
    return [];
  }
}

// ── 관리자 앱 미러 알림 (와우포스) ────────────────────────────
// 사운드 (시뮬 자산 재사용). 토글 ON 시점(사용자 제스처)에 프라이밍해 자동재생 정책 우회.
const mirrorSounds = (() => {
  try {
    return { order: new Audio(posOrderSound), call: new Audio(posCallSound), review: new Audio(posReviewSound) };
  } catch (e) { return null; }
})();
let mirrorSoundsPrimed = false;
function primeMirrorSounds() {
  if (mirrorSoundsPrimed || !mirrorSounds) return;
  mirrorSoundsPrimed = true;
  Object.values(mirrorSounds).forEach((a) => {
    try {
      a.volume = 0;
      const p = a.play();
      if (p && p.then) p.then(() => { a.pause(); a.currentTime = 0; a.volume = 1; }).catch(() => { a.volume = 1; });
    } catch (e) { /* ignore */ }
  });
}
function playMirrorSound(kind) {
  if (!mirrorSounds || !mirrorSounds[kind]) return;
  try { const a = mirrorSounds[kind]; a.currentTime = 0; a.play().catch(() => {}); } catch (e) { /* ignore */ }
}

// 주문 이벤트/서버응답 → OrderToast prop 형태로 정규화.
// 옵션의 qty/price 를 버리지 않는다 — 예전엔 name 만 남겨서 옵션 가격이
// 합계에서 통째로 빠졌다(아메리카노+시럽+샷 2800원이 1800원으로 표시).
function mapMirrorOrderItems(items) {
  return (items || []).map((it) => ({
    name: it.name,
    quantity: Number(it.qty ?? it.quantity) || 1,
    options: Array.isArray(it.options)
      ? it.options.map((o) => (o && typeof o === 'object'
          ? { name: o.name || '', quantity: Number(o.qty ?? o.quantity) || 1, price: Number(o.price) || 0 }
          : { name: String(o), quantity: 1, price: 0 }))
      : [],
    isReviewBenefit: !!(it.is_review_benefit ?? it.isReviewBenefit),
    reviewChannel: it.review_channel ?? it.reviewChannel ?? null,
    price: Number(it.price) || 0,
  }));
}
function toOrderPop(data, id) {
  const items = mapMirrorOrderItems(data.items || data.menuItems);
  const rb = items.find((it) => it.isReviewBenefit);
  // 합계는 PC 알림창(C# 클라)과 똑같이 낸다. 화면이 달라 보이면 안 된다.
  //
  //   1) 서버가 totalPrice 를 주면 그대로 쓴다.
  //      wowpos 가 이 경우다 — items[].price 에 이미 (기본가+옵션)×수량 이 들어 있어
  //      받는 쪽이 다시 계산하면 중복된다. WowPosAutoClient 도 total_price 를 그냥 쓴다.
  //   2) 없으면 C# 과 같은 식으로 계산한다.
  //        totalPrice += price;              (항목은 수량을 곱하지 않는다)
  //        totalPrice += optPrice * optQty;  (옵션만 수량을 곱한다)
  //      metacity 가 이 경우다 — price 는 기본가라 옵션을 더해야 POS 금액과 맞는다.
  const total = data.totalPrice != null
    ? Number(data.totalPrice) || 0
    : items.reduce(
        (s, it) => s + (it.price || 0)
          + (it.options || []).reduce((os, o) => os + (o.price || 0) * (o.quantity || 1), 0),
        0
      );
  return {
    id,
    orderEventId: data.id != null ? data.id : null,
    tableCode: String(data.tableCode ?? data.tableName ?? ''),
    tableName: data.tableName || data.tableCode || '',
    isNewOrder: !!(data.is_new_order ?? data.isNewOrder),
    menuItems: items,
    totalPrice: total,
    reviewBenefit: rb ? rb.name : null,
    reviewChannel: rb ? (rb.reviewChannel || 'naver') : null,
  };
}

const OrderOverview = ({ isOpen, onClose, storeId, posType }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [tables, setTables] = useState([]);
  const [cellData, setCellData] = useState({}); // table_code -> { lines, hasOrder }
  const [selectedTable, setSelectedTable] = useState(null);

  // 관리자 앱 미러 알림 (전 POS 지원, 토글).
  // 주의: 주문내역 상세의 '초기화' 버튼은 이것과 별개로 posType === 'wowpos' 로만 노출된다
  //       (타 POS 는 실제 POS 와 API 연동이라 우리가 초기화하면 안 됨). 아래 showResetButton 참고.
  const supportsMirror = !!posType;
  const [mirrorOn, setMirrorOn] = useState(() => {
    try { return localStorage.getItem('wowposMirrorNotify') === '1'; } catch (e) { return false; }
  });
  const [popOrders, setPopOrders] = useState([]);
  const [popCalls, setPopCalls] = useState([]);
  const [popReviews, setPopReviews] = useState([]);
  const popIdRef = useRef(1);
  const mirrorOnRef = useRef(mirrorOn);
  useEffect(() => { mirrorOnRef.current = mirrorOn; }, [mirrorOn]);

  const handleCloseRef = useRef(null);
  const orderDetailOpenRef = useRef(false);

  // 닫기 — history.back은 직접 호출하지 않고 popstate cleanup 에서 좀비 정리 (이중 pop 방지)
  const handleClose = useCallback(() => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 150);
  }, [onClose]);

  useEffect(() => {
    handleCloseRef.current = handleClose;
  }, [handleClose]);

  // 테이블 + 현재 주문 로드
  const loadOverview = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await apiRequest('/admin/tables');
      let linked = (res?.tables || []).filter(
        (t) => Array.isArray(t.qr_codes) && t.qr_codes.length > 0
      );
      // 태블릿 매장은 QR 을 붙이지 않아 store_tables 가 비어 있다 — POS 원본 목록으로 격자를 그린다.
      if (linked.length === 0) {
        const pos = await apiRequest('/admin/pos-tables').catch(() => null);
        linked = (pos?.tables || []).map((t) => ({ ...t, qr_codes: [] }));
      }
      setTables(linked);

      const map = {};
      if (posType === 'metacity') {
        // metacity: 8062(전체) 한 번으로 전 테이블 조회 → tableCode 로 그룹핑 (WS 왕복 1회)
        const normTc = (x) => String(x ?? '').replace(/^0+/, '') || '0';
        const byTable = {};
        try {
          const r = await getMetacityOrderInfo(storeId); // tableCode 없음 → 8062
          if (r.success) {
            (r.orders || []).forEach((o) => {
              const arr = byTable[normTc(o.tableCode)] || (byTable[normTc(o.tableCode)] = []);
              (o.items || []).forEach((it) =>
                arr.push({ name: it.prodName, quantity: Number(it.prodCount) || 1 })
              );
            });
          }
        } catch (e) {
          console.error('[주문내역 개요] metacity 전체(8062) 조회 실패:', e);
        }
        linked.forEach((t) => {
          const lines = buildLines({ menuItems: byTable[normTc(t.table_code)] || [] }, 4);
          map[t.table_code] = { lines, hasOrder: lines.length > 0 };
        });
      } else {
        // okdc / wowpos / union: 테이블별 개별 조회 (동시 최대 5)
        const menuItemsPerTable = await mapLimit(linked, 5, (t) =>
          fetchTableMenuItems(posType, storeId, t.table_code)
        );
        linked.forEach((t, idx) => {
          // 정사각형 셀에 맞춰 최대 4줄(초과 시 앞 3줄 + '…')
          const lines = buildLines({ menuItems: menuItemsPerTable[idx] }, 4);
          map[t.table_code] = { lines, hasOrder: lines.length > 0 };
        });
      }
      setCellData(map);
    } catch (e) {
      console.error('[주문내역 개요] 로드 실패:', e);
      setTables([]);
      setCellData({});
    } finally {
      setIsLoading(false);
    }
  }, [posType, storeId]);

  // 애니메이션 상태 + 최초 로드
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setTimeout(() => setIsAnimating(true), 10);
      loadOverview();
    } else {
      setIsAnimating(false);
      const timer = setTimeout(() => setIsVisible(false), 150);
      return () => clearTimeout(timer);
    }
  }, [isOpen, loadOverview]);

  // pushState — 개요 history entry 1회 추가
  useEffect(() => {
    if (!isOpen) return;
    window.history.pushState({ orderOverview: true }, '', window.location.href);
  }, [isOpen]);

  // 뒤로가기(Android + 브라우저)
  useEffect(() => {
    if (!isOpen) return;

    const handleBack = () => {
      // 상세(주문내역)가 열려있으면 상세만 닫고 개요는 유지.
      // 핸드폰 네이티브 뒤로가기(handleAndroidBack)는 history 를 pop 하기 전에 호출될 수
      // 있어 history.state 로 판별하면 개요가 잘못 닫힘 → ref 로 판별한다.
      // orderDetailOpenRef 는 effect cleanup(재렌더 후)에서 내려가므로, 한 번의 뒤로가기로
      // 발생하는 handleAndroidBack+popstate 다중 호출 동안엔 계속 true → 개요가 안 닫힘.
      if (orderDetailOpenRef.current) {
        setSelectedTable(null);
        return;
      }
      // 개요만 열린 상태: 자기 entry pop 이 아직 안 됐으면(네이티브 pre-pop 호출) 무시,
      // pop 된 뒤(popstate)엔 닫는다.
      if (window.history.state?.orderOverview) {
        return;
      }
      handleCloseRef.current?.();
    };

    window.handleAndroidBack = handleBack;
    const handlePopState = () => handleBack();
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.handleAndroidBack = null;
      window.removeEventListener('popstate', handlePopState);
      // UI 닫기 경로: 우리 entry 가 아직 top 이면 정리
      if (window.history.state?.orderOverview) {
        window.history.back();
      }
    };
  }, [isOpen]);

  // 상세 열림 상태를 ref 로 추적(뒤로가기 판별용). OrderHistory 가 open 시 자체적으로
  // { orderHistory } entry 를 push/pop 관리하므로 여기선 history 를 건드리지 않는다.
  // (여기서 history.back() 을 부르면 네이티브 pop 과 겹쳐 개요까지 닫히는 오버슈트 발생)
  useEffect(() => {
    if (!selectedTable) return;
    orderDetailOpenRef.current = true;
    return () => {
      orderDetailOpenRef.current = false;
    };
  }, [selectedTable]);

  // 화면 스케일
  useEffect(() => {
    const scaleUI = () => {
      const wrapper = document.querySelector('.order-overview-wrapper');
      if (wrapper) {
        const scale = Math.min(window.innerWidth / 360, 1.5);
        wrapper.style.zoom = scale;
      }
    };
    if (isVisible) {
      scaleUI();
      window.addEventListener('resize', scaleUI);
    }
    return () => window.removeEventListener('resize', scaleUI);
  }, [isVisible]);

  const openDetail = (table) => setSelectedTable(table);
  const closeDetail = useCallback(() => setSelectedTable(null), []);

  // 와우포스 실시간 반영 — 주문 도착/초기화 시 요약 화면만 갱신.
  // connect/disconnect 는 adminDashboard 가 같은 store 로 이미 소유하므로 콜백만 등록/해제.
  useEffect(() => {
    if (!isOpen || !supportsMirror) return undefined;

    wsService.onWowposOrder(async (data) => {
      if (data?.tableCode == null) return;
      const tc = String(data.tableCode);
      const items = await fetchTableMenuItems(posType, storeId, tc);
      const lines = buildLines({ menuItems: items }, 4);
      setCellData((prev) => ({ ...prev, [tc]: { lines, hasOrder: lines.length > 0 } }));
      // 미러 토글 ON → 중앙 주문 팝업 추가 + 사운드
      if (mirrorOnRef.current) {
        setPopOrders((prev) => [...prev, toOrderPop(data, popIdRef.current++)]);
        playMirrorSound('order');
      }
    });
    wsService.onWowposTableCleared((data) => {
      if (data?.tableCode == null) return;
      const tc = String(data.tableCode);
      setCellData((prev) => ({ ...prev, [tc]: { lines: [], hasOrder: false } }));
      // 초기화된 테이블의 미러 주문 팝업 제거
      setPopOrders((prev) => prev.filter((o) => String(o.tableCode) !== tc));
    });
    wsService.onWowposCall((data) => {
      if (!mirrorOnRef.current) return;
      setPopCalls((prev) => [...prev, {
        id: popIdRef.current++, batchId: (data && data.batchId) || null, tableCode: (data && data.tableCode) || '', tableName: (data && data.tableName) || '', callItems: (data && data.callItems) || [],
      }]);
      playMirrorSound('call');
    });
    wsService.onWowposReview((data) => {
      if (!mirrorOnRef.current) return;
      setPopReviews((prev) => [...prev, {
        id: popIdRef.current++, reviewId: (data && data.id) != null ? data.id : null, tableName: (data && data.tableName) || '', serviceMenu: (data && data.serviceMenu) || '', hasPhoto: !!(data && data.hasPhoto), channel: (data && data.channel) || 'naver',
      }]);
      playMirrorSound('review');
    });
    // PC/다른 앱에서 팝업을 지우면 이 앱에서도 제거 (양방향 동기화)
    wsService.onWowposNotifyRemove((data) => {
      if (!data || !data.kind) return;
      // id 는 bigint(문자열)/number 혼재 가능 → String 비교로 통일
      if (data.kind === 'call' && data.batchId) {
        setPopCalls((prev) => prev.filter((c) => c.batchId !== data.batchId));
      } else if (data.kind === 'order' && data.orderEventId != null) {
        setPopOrders((prev) => prev.filter((o) => String(o.orderEventId) !== String(data.orderEventId)));
      } else if (data.kind === 'review' && data.reviewEventId != null) {
        setPopReviews((prev) => prev.filter((r) => String(r.reviewId) !== String(data.reviewEventId)));
      }
    });

    return () => {
      wsService.onWowposOrder(null);
      wsService.onWowposTableCleared(null);
      wsService.onWowposCall(null);
      wsService.onWowposReview(null);
      wsService.onWowposNotifyRemove(null);
    };
  }, [isOpen, supportsMirror, posType, storeId]);

  // 재진입 재유도 — 토글 ON + (mount / 포그라운드 / 소켓 reconnect) 시 미처리 주문·최근 호출/리뷰 다시 팝업.
  const rederiveMirror = useCallback(async () => {
    if (!supportsMirror || !mirrorOnRef.current) return;
    try {
      const r = await fetchWowposAppNotifications(storeId);
      if (!r?.success) return;
      setPopOrders((r.orders || []).map((o) => toOrderPop(o, popIdRef.current++)));
      setPopCalls((r.calls || []).map((c) => ({
        id: popIdRef.current++, batchId: c.batchId || null, tableCode: c.tableCode || '', tableName: c.tableName || '', callItems: c.callItems || [],
      })));
      setPopReviews((r.reviews || []).map((rv) => ({
        id: popIdRef.current++, reviewId: rv.id != null ? rv.id : null, tableName: rv.tableName || '', serviceMenu: rv.serviceMenu || '', hasPhoto: !!rv.hasPhoto, channel: rv.channel || 'naver',
      })));
    } catch (e) {
      console.error('[미러] 재유도 실패:', e);
    }
  }, [supportsMirror, storeId]);

  useEffect(() => {
    if (!isOpen || !supportsMirror || !mirrorOn) {
      setPopOrders([]); setPopCalls([]); setPopReviews([]);
      return undefined;
    }
    rederiveMirror();
    const onVis = () => { if (document.visibilityState === 'visible') rederiveMirror(); };
    document.addEventListener('visibilitychange', onVis);
    const sock = wsService.socket;
    const onReconnect = () => rederiveMirror();
    if (sock && sock.on) sock.on('connect', onReconnect);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      if (sock && sock.off) sock.off('connect', onReconnect);
    };
  }, [isOpen, supportsMirror, mirrorOn, rederiveMirror]);

  const toggleMirror = useCallback(() => {
    setMirrorOn((prev) => {
      const next = !prev;
      try { localStorage.setItem('wowposMirrorNotify', next ? '1' : '0'); } catch (e) { /* ignore */ }
      if (next) primeMirrorSounds();
      return next;
    });
  }, []);

  // 팝업 dismiss — 로컬 제거 + 서버에 처리 표기(재진입 시 다시 안 뜨게).
  const handleDismissOrder = useCallback((id) => {
    setPopOrders((prev) => {
      const target = prev.find((o) => o.id === id);
      if (target?.orderEventId != null) {
        dismissWowposOrderEvent(target.orderEventId).catch((e) => console.error('[미러] 주문 dismiss 실패:', e));
      }
      return prev.filter((o) => o.id !== id);
    });
  }, []);

  const handleDismissCall = useCallback((id) => {
    setPopCalls((prev) => {
      const target = prev.find((c) => c.id === id);
      if (target?.batchId) {
        resolveWowposCall(storeId, target.batchId).catch((e) => console.error('[미러] 호출 resolve 실패:', e));
      }
      return prev.filter((c) => c.id !== id);
    });
  }, [storeId]);

  const handleDismissReview = useCallback((id) => {
    setPopReviews((prev) => {
      const target = prev.find((r) => r.id === id);
      if (target?.reviewId != null) {
        dismissWowposReview(target.reviewId).catch((e) => console.error('[미러] 리뷰 dismiss 실패:', e));
      }
      return prev.filter((r) => r.id !== id);
    });
  }, [storeId]);

  if (!isVisible) return null;

  return (
    <div className={`order-overview ${isAnimating ? 'open' : ''}`}>
      <div className="order-overview-wrapper">
        <div className="oo-header">
          <button type="button" className="oo-back" onClick={handleClose} aria-label="뒤로">
            <img src={backIcon} alt="뒤로" />
          </button>
          <span className="oo-title">주문내역</span>
          {supportsMirror ? (
            <button
              type="button"
              className={`oo-mirror-toggle ${mirrorOn ? 'on' : ''}`}
              onClick={toggleMirror}
              role="switch"
              aria-checked={mirrorOn}
              aria-label="알림 미러"
              title="주문·호출·리뷰 알림을 이 앱에도 표시"
            >
              <span className="oo-mirror-knob" />
            </button>
          ) : (
            <button
              type="button"
              className={`oo-refresh ${isLoading ? 'spinning' : ''}`}
              onClick={loadOverview}
              disabled={isLoading}
              aria-label="새로고침"
            >
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path
                  fill="#535353"
                  d="M17.65 6.35A7.96 7.96 0 0 0 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0 1 12 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4z"
                />
              </svg>
            </button>
          )}
        </div>

        <div className="oo-body">
          {isLoading && tables.length === 0 ? (
            <div className="oo-loading">
              <span className="oo-spinner" />
              <span className="oo-loading-text">주문 정보를 불러오는 중…</span>
            </div>
          ) : tables.length === 0 ? (
            <div className="oo-empty">표시할 테이블이 없습니다.</div>
          ) : (
            <div className="oo-grid">
              {tables.map((t) => {
                const cell = cellData[t.table_code] || { lines: [], hasOrder: false };
                return (
                  <button
                    type="button"
                    key={t.table_code}
                    className="oo-cell"
                    onClick={() => openDetail(t)}
                  >
                    <div className="oo-cell-head">
                      <span className="oo-cell-name">{t.table_name || t.table_code}</span>
                      {cell.hasOrder && <span className="oo-dot" />}
                    </div>
                    <div className="oo-cell-lines">
                      {cell.lines.map((ln, i) => (
                        <div key={i} className="oo-line">
                          <span className="oo-line-name">{ln.name}</span>
                          <span className="oo-line-qty">{ln.qty}</span>
                        </div>
                      ))}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* 상세 주문내역 — 손님 orderHistory UI 재사용 (옵션 포함) */}
      <OrderHistory
        isOpen={!!selectedTable}
        storeId={storeId}
        posType={posType}
        tableCode={selectedTable?.table_code || '1'}
        tableName={selectedTable?.table_name || null}
        selectedLanguage="ko"
        bottomButtonLabel="뒤로가기"
        hideOrderButton={true}
        showResetButton={posType === 'wowpos'}
        showCancelButton={posType === 'wowpos'}
        onClose={closeDetail}
      />

      {/* 와우포스 관리자 앱 미러 알림 레이어 (토글 ON). portal 로 body 에 렌더 */}
      {supportsMirror && mirrorOn && (
        <WowposNotifyLayer
          orders={popOrders}
          calls={popCalls}
          reviews={popReviews}
          onDismissOrder={handleDismissOrder}
          onDismissCall={handleDismissCall}
          onDismissReview={handleDismissReview}
        />
      )}

      <style jsx>{`
        .order-overview {
          position: fixed;
          inset: 0;
          z-index: 10000;
          background: #ffffff;
          transform: translateX(100%);
          transition: transform 0.15s cubic-bezier(0.4, 0, 0.2, 1);
          font-family: 'Pretendard Variable', Pretendard, -apple-system, system-ui, sans-serif;
        }
        .order-overview.open {
          transform: translateX(0);
        }
        .order-overview-wrapper {
          width: 360px;
          height: 100%;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          background: #ffffff;
        }
        .oo-header {
          display: flex;
          align-items: center;
          height: 56px;
          padding: 0 8px;
          border-bottom: 1px solid #f0f0f0;
          flex-shrink: 0;
        }
        .oo-back {
          width: 40px;
          height: 40px;
          border: none;
          background: transparent;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
        }
        .oo-back img {
          width: 22px;
          height: 22px;
        }
        .oo-title {
          flex: 1;
          text-align: center;
          font-size: 17px;
          font-weight: 700;
          color: #2b2b2b;
        }
        .oo-refresh {
          width: 40px;
          height: 40px;
          border: none;
          background: transparent;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          border-radius: 8px;
          -webkit-tap-highlight-color: transparent;
        }
        .oo-mirror-toggle {
          position: relative;
          width: 44px;
          height: 26px;
          flex-shrink: 0;
          border: none;
          border-radius: 13px;
          background: #d4d4d8;
          cursor: pointer;
          padding: 0;
          transition: background 0.15s;
          -webkit-tap-highlight-color: transparent;
        }
        .oo-mirror-toggle.on {
          background: #fdcd27;
        }
        .oo-mirror-knob {
          position: absolute;
          top: 3px;
          left: 3px;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #ffffff;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
          transition: transform 0.15s;
        }
        .oo-mirror-toggle.on .oo-mirror-knob {
          transform: translateX(18px);
        }
        .oo-refresh:disabled {
          cursor: default;
        }
        .oo-refresh.spinning svg {
          animation: oo-spin 0.8s linear infinite;
        }
        @keyframes oo-spin {
          to {
            transform: rotate(360deg);
          }
        }
        .oo-body {
          flex: 1;
          overflow-y: auto;
          padding: 14px 12px 24px;
        }
        .oo-loading,
        .oo-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 12px;
          padding-top: 80px;
          color: #9a9a9a;
          font-size: 14px;
        }
        .oo-spinner {
          width: 26px;
          height: 26px;
          border: 3px solid #eee;
          border-top-color: #fdcd27;
          border-radius: 50%;
          animation: oo-spin 0.8s linear infinite;
        }
        .oo-loading-text {
          font-size: 13px;
        }
        .oo-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 8px;
        }
        .oo-cell {
          aspect-ratio: 1 / 1;
          min-height: 0;
          background: #fafafb;
          border: 1px solid #ececec;
          border-radius: 12px;
          cursor: pointer;
          overflow: hidden;
          padding: 9px 9px;
          text-align: left;
          transition: background 120ms, border-color 120ms;
          display: flex;
          flex-direction: column;
          -webkit-tap-highlight-color: transparent;
        }
        /* hover 효과는 실제 마우스 기기에서만 — 터치 기기에서 남는(sticky) 하이라이트 방지 */
        @media (hover: hover) {
          .oo-cell:hover {
            background: #f4f4f5;
            border-color: #dedede;
          }
        }
        .oo-cell-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 7px;
        }
        .oo-cell-name {
          font-size: 14px;
          font-weight: 700;
          color: #2b2b2b;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .oo-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #ff4d4f;
          flex-shrink: 0;
          margin-left: 6px;
        }
        .oo-cell-lines {
          flex: 1;
          min-height: 0;
        }
        .oo-line {
          display: flex;
          align-items: center;
          margin-bottom: 2px;
        }
        .oo-line-name {
          flex: 1;
          min-width: 0;
          font-size: 11.5px;
          color: #6b6b6b;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .oo-line-qty {
          margin-left: 8px;
          font-size: 11.5px;
          color: #6b6b6b;
        }
      `}</style>
    </div>
  );
};

export default OrderOverview;
